import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import type { ProfitProductRow, Profitability } from '../components/profitability/types';

// Rentabilidad por producto: cuánto deja cada producto con su costo directo (receta + comisión del canal) y con los
// costos indirectos (gastos fijos y del período) repartidos. Pedido de Eric, 03/11/2026.

const money = (n?: number | null) => (n == null ? '—' : `$${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Math.round(n))}`);
const pct = (n?: number | null) => (n == null ? '—' : `${(n * 100).toFixed(1).replace('.', ',')}%`);
const qty = (n: number) => new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(n);
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

type Preset = 'mes' | 'mes-anterior' | '3-meses' | 'anio' | 'custom';
export function presetRange(p: Preset, custom?: { from: string; to: string }): { from: string; to: string } {
  const t = todayIso();
  const [y, m] = t.split('-').map(Number);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  if (p === 'mes') return { from: `${t.slice(0, 7)}-01`, to: t };
  if (p === 'mes-anterior') return { from: iso(new Date(Date.UTC(y, m - 2, 1))), to: iso(new Date(Date.UTC(y, m - 1, 0))) };
  if (p === '3-meses') return { from: iso(new Date(Date.UTC(y, m - 3, 1))), to: t };
  if (p === 'anio') return { from: `${y}-01-01`, to: t };
  return custom ?? { from: `${t.slice(0, 7)}-01`, to: t };
}

type SortKey = 'netProfit' | 'contribution' | 'revenueNet' | 'contributionPct' | 'netPct' | 'units' | 'recipeMarginPct';

const SOURCE: Record<string, { label: string; color: 'success' | 'info' | 'warning' | 'error' }> = {
  receta: { label: 'Receta', color: 'success' },
  producto: { label: 'Costo del producto', color: 'info' },
  'sin-costo': { label: 'Sin costo', color: 'error' },
};

const ProfitabilityPage: React.FC = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset>('mes');
  const [custom, setCustom] = useState({ from: presetRange('mes').from, to: todayIso() });
  const [allocation, setAllocation] = useState<'revenue' | 'units'>('revenue');
  const [data, setData] = useState<Profitability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'netProfit', dir: 'desc' });
  const [showMissing, setShowMissing] = useState(false);

  const range = useMemo(() => presetRange(preset, custom), [preset, custom]);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setData(await api.getProfitability({ ...range, allocation })); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo calcular la rentabilidad'); }
    finally { setLoading(false); }
  }, [range, allocation]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = [...(data?.products ?? [])];
    list.sort((a, b) => (sort.dir === 'desc' ? 1 : -1) * ((b[sort.key] as number) - (a[sort.key] as number)));
    return list;
  }, [data, sort]);
  const t = data?.totals;
  const head = (key: SortKey, label: string, tip?: string) => (
    <TableCell align="right" sortDirection={sort.key === key ? sort.dir : false}>
      <Tooltip title={tip || ''}><TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'desc'}
        onClick={() => setSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}>{label}</TableSortLabel></Tooltip>
    </TableCell>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight={700}>Rentabilidad</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '80ch' }}>
        Cuánto deja cada producto. Costo directo: lo que lleva la receta (materia prima, envases, insumos) más la comisión del canal.
        Costos indirectos: los gastos fijos del mes y los gastos del período, repartidos entre los productos. Todo neto de IVA.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_, v) => v && setPreset(v)}>
          <ToggleButton value="mes">Este mes</ToggleButton>
          <ToggleButton value="mes-anterior">Mes anterior</ToggleButton>
          <ToggleButton value="3-meses">Últimos 3 meses</ToggleButton>
          <ToggleButton value="anio">Este año</ToggleButton>
          <ToggleButton value="custom">Elegir</ToggleButton>
        </ToggleButtonGroup>
        {preset === 'custom' && (
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={custom.from} onChange={e => setCustom({ ...custom, from: e.target.value })} />
            <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={custom.to} onChange={e => setCustom({ ...custom, to: e.target.value })} />
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Cómo se reparten los costos indirectos entre los productos">
          <ToggleButtonGroup size="small" exclusive value={allocation} onChange={(_, v) => v && setAllocation(v)}>
            <ToggleButton value="revenue">Indirectos por ventas</ToggleButton>
            <ToggleButton value="units">Por unidades</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && <CircularProgress />}

      {t && (
        <>
          {t.costCoverage < 0.95 && (
            <Alert severity="warning" sx={{ mb: 2 }}
              action={data!.missingCosts.length > 0 ? <Button size="small" onClick={() => setShowMissing(s => !s)}>{showMissing ? 'Ocultar' : `Ver qué falta (${data!.missingCosts.length})`}</Button> : undefined}>
              Solo el <b>{pct(t.costCoverage)}</b> de las ventas tiene el costo completo. Lo que no tiene costo cuenta como $0, así que la ganancia
              aparece más alta de lo real. Cargá el costo de las materias primas (o del producto) para que el número sea confiable.
            </Alert>
          )}
          <Collapse in={showMissing}>
            <Paper variant="outlined" sx={{ mb: 2, maxHeight: 280, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Falta el costo de</TableCell><TableCell>Tipo</TableCell><TableCell align="right">Productos que lo usan</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>
                  {data!.missingCosts.map(m => (
                    <TableRow key={`${m.kind}-${m.id}`}>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>{m.kind === 'material' ? 'Materia prima' : m.kind === 'insumo' ? 'Insumo' : 'Producto sin receta'}</TableCell>
                      <TableCell align="right">{m.usedInProducts}</TableCell>
                      <TableCell align="right"><Button size="small" onClick={() => navigate(m.kind === 'material' ? '/materials' : m.kind === 'insumo' ? '/insumos' : '/products')}>Cargar</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Collapse>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} divider={<Box sx={{ borderLeft: { md: '1px solid' }, borderColor: 'divider' }} />}>
              <Kpi label="Ventas netas" value={money(t.revenueNet)} hint={`${t.sales} ventas · ${qty(t.units)} u. · IVA ${money(t.iva)}`} />
              <Kpi label="Costo directo" value={money(t.directCost)} hint={`producción ${money(t.productionCost)} · comisiones ${money(t.sellingCost)}`} />
              <Kpi label="Contribución" value={money(t.contribution)} hint={`${pct(t.contributionPct)} de las ventas`} tone={t.contribution >= 0 ? 'ok' : 'bad'} />
              <Kpi label="Costos indirectos" value={money(t.indirectCost)} hint="gastos fijos + gastos del período" />
              <Kpi label="Ganancia neta" value={money(t.netProfit)} hint={`${pct(t.netPct)} de las ventas`} tone={t.netProfit >= 0 ? 'ok' : 'bad'} />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Typography sx={{ px: 2, pt: 1.5, fontWeight: 650 }}>Por producto</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block', pb: 1 }}>
              Contribución = ventas netas − costo directo. Ganancia neta = contribución − su parte de los costos indirectos ({allocation === 'units' ? 'según unidades vendidas' : 'según lo que vendió'}).
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    {head('units', 'Unidades')}
                    {head('revenueNet', 'Ventas netas')}
                    <TableCell align="right">Precio prom.</TableCell>
                    <TableCell align="right">Costo unitario</TableCell>
                    {head('recipeMarginPct', 'Rentab. s/receta', '(precio promedio neto − costo unitario de la receta) / precio. Sin comisiones ni gastos fijos.')}
                    <TableCell align="right">Comisiones</TableCell>
                    {head('contribution', 'Contribución', 'Ventas netas − costo de producción − comisiones')}
                    {head('contributionPct', 'Contrib. %')}
                    <TableCell align="right">Indirectos</TableCell>
                    {head('netProfit', 'Ganancia neta')}
                    {head('netPct', 'Neta %')}
                    <TableCell align="right">Por unidad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && <TableRow><TableCell colSpan={13} sx={{ color: 'text.secondary', py: 3 }} align="center">No hay ventas en el período.</TableCell></TableRow>}
                  {rows.map((r: ProfitProductRow) => (
                    <TableRow key={r.productId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip size="small" label={SOURCE[r.costSource]?.label ?? r.costSource} color={r.costIncomplete && r.costSource === 'receta' ? 'warning' : SOURCE[r.costSource]?.color} variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                          {r.costIncomplete && r.costSource === 'receta' && <Typography variant="caption" color="warning.main">receta con componentes sin costo</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{qty(r.units)}</TableCell>
                      <TableCell align="right">{money(r.revenueNet)}</TableCell>
                      <TableCell align="right">{money(r.avgUnitPrice)}</TableCell>
                      <TableCell align="right">{money(r.unitDirectCost)}</TableCell>
                      <TableCell align="right">
                        {r.unitDirectCost > 0 ? (
                          <Tooltip title={r.recipeMarkupPct != null ? `Markup sobre el costo: ${pct(r.recipeMarkupPct)}` : ''}>
                            <Chip size="small" label={pct(r.recipeMarginPct)} color={r.costIncomplete ? 'warning' : r.recipeMarginPct >= 0.4 ? 'success' : r.recipeMarginPct >= 0.2 ? 'default' : 'error'} variant={r.costIncomplete ? 'outlined' : 'filled'} />
                          </Tooltip>
                        ) : <Typography variant="caption" color="text.secondary">sin costo</Typography>}
                      </TableCell>
                      <TableCell align="right">{money(r.sellingCost)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: r.contribution < 0 ? 'error.main' : undefined }}>{money(r.contribution)}</TableCell>
                      <TableCell align="right">{pct(r.contributionPct)}</TableCell>
                      <TableCell align="right">{money(r.indirectAllocated)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: r.netProfit < 0 ? 'error.main' : 'success.main' }}>{money(r.netProfit)}</TableCell>
                      <TableCell align="right">{pct(r.netPct)}</TableCell>
                      <TableCell align="right">{money(r.netPerUnit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined">
            <Typography sx={{ px: 2, pt: 1.5, fontWeight: 650 }}>Costos indirectos del período</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block', pb: 1 }}>
              Los gastos fijos mensuales se prorratean por los días del período; los gastos de una vez entran por su fecha.
            </Typography>
            <Table size="small">
              <TableBody>
                {data!.indirect.length === 0 && <TableRow><TableCell sx={{ color: 'text.secondary' }}>No hay gastos cargados.</TableCell></TableRow>}
                {data!.indirect.map((i, k) => (
                  <TableRow key={k}>
                    <TableCell>{i.name}</TableCell>
                    <TableCell>{i.category}</TableCell>
                    <TableCell>{i.kind === 'fijo' ? 'Fijo mensual' : 'Del período'}</TableCell>
                    <TableCell align="right">{money(i.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

const Kpi: React.FC<{ label: string; value: string; hint?: string; tone?: 'ok' | 'bad' }> = ({ label, value, hint, tone }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: tone === 'bad' ? 'error.main' : tone === 'ok' ? 'success.main' : undefined }}>{value}</Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Box>
);

export default ProfitabilityPage;
